/// <amd-module name="Build/Extensions/GitHub/RepositorySelection" />
import * as React from "react";

import * as Build from "Build/Client/Contracts/Build";
import { FilterItem, Props as BaseProps, RepositoriesListData, RepositorySelector } from "Build/Designer/RepositorySelector";
import { ServiceConnectionData } from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";
import * as Resources from "Build/Extensions/GitHub/Resources";

import * as StringUtil from "VSS/Core/Util/String";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { IVssComponentContext } from "VSS/Platform/Layout";

export interface Props extends BaseProps {
    serviceName: string;
}

export class RepositorySelection extends RepositorySelector<Props> {
    private _link: string;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        if (!this.autoSelectConnection()) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            const dataProviderId = "ms.vss-build-web.github-serviceconnections-data-provider";
            contributionService.getDataAsync<ServiceConnectionData>(dataProviderId, { "endpointType": this.props.sourceProvider }, true)
            .then((serviceConnectionData) => {
                if (this.checkIsMounted()) {
                    if (serviceConnectionData) {
                        for (const connection of serviceConnectionData.serviceConnections) {
                            if (connection.id === this.props.connectionId) {
                                this._link = connection.manageAccessUrl;
                                this.setState({
                                    footerText: this.getFooter(this.state.filterNoReposFound, !!(this.state.repositoryList && this.state.repositoryList.continuationToken))
                                });
                                break;
                            }
                        }
                    } else {
                        const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                        this.props.onErrorMessage(exception && exception.message);
                    }
                }
            }).catch((error: Error) => {
                this.props.onErrorMessage(error && error.message);
            });
        }
    }

    protected getRepositories(
        serviceEndpointId?: string,
        filter?: string,
        resultSet?: string,
        pageResults?: boolean,
        continuationToken?: string): Promise<Build.SourceRepositories> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const parameters = { resultSet: resultSet, pageResults: pageResults, continuationToken: continuationToken, sourceProvider: this.props.sourceProvider } as {[key: string]: any};
        if (serviceEndpointId) {
            parameters.serviceEndpointId = serviceEndpointId;
        } else {
            parameters.strongBoxKey = this.props.strongBoxKey!;
        }
        return contributionService.getDataAsync<RepositoriesListData>(
            "ms.vss-build-web.github-repositories-data-provider",
            parameters, true /* ignore cache */).then(x => {
                this._link = x!.manageAccessUrl || "";
                return x! as Build.SourceRepositories;
            });
    }

    protected getClassName(baseClassName: string): string {
        return baseClassName + " github";
    }

    protected getFilterItems(): FilterItem[] {
        if (this.autoSelectConnection()) {
            return [];
        }
        return [
            { key: Build.ResultSet.Top, displayName: Resources.TopRepositories },
            { key: Build.ResultSet.All, displayName: Resources.AllRepositories },
        ];
    }

    protected getDescriptionMessage = (): string => {
        if (this.state && this.state.repositoryList && this.state.repositoryList.continuationToken) {
            const approxTotalRepos = this.state.repositoryList.repositories.length * this.state.repositoryList.totalPageCount;
            return StringUtil.format(Resources.FetchingRepositories, approxTotalRepos && approxTotalRepos.toLocaleString(), this.props.serviceName);
        }
        return "";
    }

    protected getFooter(filterNoReposFound: boolean, downloading: boolean): JSX.Element | undefined {
        let appendSelectConnectionLink = (element: JSX.Element | undefined): JSX.Element | undefined => element;

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentState = historyService.getState();

        if (this.autoSelectConnection()) {
            if (!!currentState["connectionId"]) {
                delete currentState["connectionId"];
            }
            if (!!currentState["repositoryId"]) {
                delete currentState["repositoryId"];
            }
            if (!!currentState["key"]) {
                delete currentState["key"];
            }
            const url: string = historyService.generateUrl({
                ...currentState,
                showSelectConnection: "true"
            }, StateMergeOptions.none)

            appendSelectConnectionLink = (element: JSX.Element | undefined): JSX.Element | undefined => {
                return <div>{element}
                    <FormatComponent format={Resources.SelectServiceConnectionMessage}>
                        {<FPSLink href={url}
                            telemetrySource={"new-pipeline-wizard.select-connection"}
                            telemetryProperties={{ telemetrySession: currentState.telemetrySession }}>
                            {Resources.SelectServiceConnectionMessageLink}
                        </FPSLink>}
                    </FormatComponent></div>
            };
        }

        if (downloading) {
            return appendSelectConnectionLink(undefined);
        }
        else if (filterNoReposFound) {
            return appendSelectConnectionLink(super.getFooter(filterNoReposFound, downloading));
        }

        const link = this.getFooterLink();
        return appendSelectConnectionLink(<div>
            {this.state.resultSet === Build.ResultSet.Top ? Resources.TopRepositoriesDescription : Resources.AllRepositoriesDescription}
            {this._link && <div>
                <FormatComponent format={Resources.GitHubGrantPermissionDescription}>
                    {<FPSLink href={link}
                        supportsFPS={false} // guaranteed to be an external link
                        telemetrySource={"new-pipeline-wizard.github-grant-permissions"}
                        telemetryProperties={{ telemetrySession: currentState.telemetrySession }}>
                        {Resources.GitHubGrantPermissionDescriptionLink}
                    </FPSLink>}
                </FormatComponent>
            </div>}
        </div>);
    }

    protected getFooterLink(): string | undefined {
        return this._link;
    }

    protected getRepositoryIconName(): string {
        return "GitHubLogo";
    }
}
